import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { ErrorBoundary } from './components/ErrorBoundary';

/** Sonde de vérification : lève l'erreur DOM typique d'une traduction automatique
 *  pour contrôler que l'écran de secours et le rechargement auto fonctionnent. */
function DomErrorProbe() {
  const err = new Error("Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.");
  err.name = 'NotFoundError';
  throw err;
}
import Login from './pages/Login';
import Layout from './pages/Layout';
import ResetPassword from './pages/ResetPassword';
import CompletionForm from './pages/CompletionForm';
import AgencySignup from './pages/AgencySignup';
import OctopusPrerequisPage from './pages/OctopusPrerequisPage';
import SigningFlow from './pages/SigningFlow';
// Loaders centralisés (utilisés à la fois pour lazy() ici et pour le prefetch
// au survol de la sidebar côté Layout.jsx).
import { routeLoaders } from './lib/routePrefetch';
const Dashboard = lazy(routeLoaders['/dashboard']);
const Secteurs = lazy(routeLoaders['/secteurs']);
const ContractsList = lazy(routeLoaders['/contracts']);
const SuiviClient = lazy(routeLoaders['/suivi-client']);
const Objectifs = lazy(routeLoaders['/objectifs']);
const CommissionDashboard = lazy(routeLoaders['/commission-dashboard']);
const MesObjectifs = lazy(routeLoaders['/mes-objectifs']);
const AgenceObjectifMap = lazy(routeLoaders['/agence-map']);
const SellerSpace = lazy(routeLoaders['/seller-space']);
const Admin = lazy(routeLoaders['/admin']);
const Outils = lazy(routeLoaders['/outils']);
const Infos = lazy(routeLoaders['/infos']);
const Training = lazy(routeLoaders['/formation']);
const OctopusPresentation = lazy(routeLoaders['/formation/octopus-energy']);
const IlekPresentation = lazy(routeLoaders['/formation/ilek']);
const QuizOctopusVente = lazy(routeLoaders['/formation/quiz-octopus-vente']);
const OrganigrammePage = lazy(routeLoaders['/organigramme']);
const OrganisationAdmin = lazy(routeLoaders['/organisation']);
const Voitures = lazy(routeLoaders['/voitures']);
const FuelDashboard = lazy(routeLoaders['/fuel']);
const DatabaseAdmin = lazy(routeLoaders['/db-admin']);
const SuiviCartons = lazy(routeLoaders['/suivi-cartons']);
const Chat = lazy(routeLoaders['/chat']);
const QualityOverview = lazy(routeLoaders['/quality-overview']);
const IlekDashboard = lazy(routeLoaders['/ilek/dashboard']);
const IlekContracts = lazy(routeLoaders['/ilek/contracts']);
const IlekQualite   = lazy(routeLoaders['/ilek/qualite']);
const BouyguesDashboard = lazy(routeLoaders['/bouygues/dashboard']);
const BouyguesContracts = lazy(routeLoaders['/bouygues/contracts']);
const MesDocuments = lazy(routeLoaders['/mes-documents']);
const AssistantIA = lazy(routeLoaders['/assistant-ia']);
import '@/App.css';

// Fallback léger pour les pages lazy-loaded.
function PageLoader() {
  return (
    <div className='flex items-center justify-center h-[60vh]' data-testid='page-loader'>
      <div className='text-muted-foreground text-sm'>Chargement…</div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles, pageKey, requirePartner, bypassIfAgence, bypassIfEmail }) {
  const { user, loading } = useAuth();
  const { canAccess, myPages } = usePermissions();

  if (loading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='text-muted-foreground'>Chargement...</div>
      </div>
    );
  }

  if (!user) return <Navigate to='/login' replace />;

  // Dérogation par agence (ex: "Maison mère" pour Suivi par Agence) — bypass rôle + RBAC
  const agenceMatch = bypassIfAgence && (user.agence_ville || '').trim().toLowerCase() === bypassIfAgence.toLowerCase();
  // Dérogation par email (ex: admin maître, garde toujours accès à certaines pages) — bypass RBAC
  const emailMatch = bypassIfEmail && (user.email || '').toLowerCase() === bypassIfEmail.toLowerCase();

  // Hard role lock (admin panel, db-admin, etc.)
  if (!agenceMatch && allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to='/' replace />;
  }

  // RÈGLE #1 — Partner scoping prévaut sur le RBAC.
  // Si la route exige un partenaire (ex: 'octopus', 'ilek') et que l'user n'y est
  // pas rattaché → redirect vers la racine (RootRedirect choisira la bonne page).
  if (requirePartner) {
    const slugs = (user.allowed_partners || []).map(p => p.slug);
    if (user.allowed_partners && !slugs.includes(requirePartner)) {
      return <Navigate to='/' replace />;
    }
  }

  // Permission-based access (only block once permissions are loaded)
  if (!agenceMatch && !emailMatch && pageKey && myPages !== null && !canAccess(pageKey)) {
    return <Navigate to='/' replace />;
  }

  return children;
}

// Composant d'entrée : redirige vers la 1re page accessible selon les partenaires de l'user.
function RootRedirect() {
  const { user, logout } = useAuth();
  if (!user) return <Navigate to='/login' replace />;
  const slugs = (user.allowed_partners || []).map(p => p.slug);
  // Pas de allowed_partners exposé → rétrocompat : dashboard Octopus
  if (!user.allowed_partners) return <Navigate to='/dashboard' replace />;
  if (slugs.includes('octopus')) return <Navigate to='/dashboard' replace />;
  if (slugs.includes('ilek')) return <Navigate to='/ilek/dashboard' replace />;
  // User connecté mais sans partenaire rattaché : NE PAS reboucler vers /login
  // (sinon boucle infinie quand le compte est mal configuré côté DB).
  // On affiche un message explicite + un bouton de déconnexion.
  return (
    <div className='flex items-center justify-center min-h-svh p-6'>
      <div className='max-w-md text-center space-y-4'>
        <h1 className='text-2xl font-semibold'>Aucun partenaire rattaché</h1>
        <p className='text-muted-foreground'>
          Votre compte est authentifié ({user.email}) mais n'est rattaché à aucun
          partenaire (Octopus / ILEK). Contactez un administrateur du Back-Office
          pour activer vos accès.
        </p>
        <button
          onClick={logout}
          className='px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90'
          data-testid='no-partner-logout-button'
        >
          Se déconnecter
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <PermissionsProvider>
          <Routes>
          <Route path='/login' element={<Login />} />
          <Route path='/reset-password' element={<ResetPassword />} />
          <Route path='/completion/:token' element={<CompletionForm />} />
          <Route path='/inscription/:token' element={<AgencySignup />} />
          <Route path='/prerequis/:token' element={<OctopusPrerequisPage />} />
          <Route path='/signing/:token' element={<SigningFlow />} />
          
          <Route
            path='/'
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<RootRedirect />} />
            {/* Sonde de test du filet de sécurité anti-traduction (/dom-error-probe) :
                reproduit l'erreur DOM que provoque Google Translate sous React. */}
            <Route path='dom-error-probe' element={<ErrorBoundary><DomErrorProbe /></ErrorBoundary>} />
            <Route element={<ErrorBoundary><Suspense fallback={<PageLoader />}><Outlet /></Suspense></ErrorBoundary>}>
            <Route path='dashboard' element={<ProtectedRoute requirePartner="octopus"><Dashboard /></ProtectedRoute>} />
            <Route path='secteurs' element={<ProtectedRoute pageKey="secteurs" requirePartner="octopus"><Secteurs /></ProtectedRoute>} />
            <Route path='contracts' element={<ProtectedRoute pageKey="contracts" requirePartner="octopus"><ContractsList /></ProtectedRoute>} />
            <Route path='suivi-client' element={<ProtectedRoute pageKey="suivi-client" requirePartner="octopus"><SuiviClient /></ProtectedRoute>} />
            <Route path='objectifs' element={<ProtectedRoute pageKey="objectifs" requirePartner="octopus"><Objectifs /></ProtectedRoute>} />
            <Route path='mes-objectifs' element={<ProtectedRoute pageKey="mes-objectifs"><MesObjectifs /></ProtectedRoute>} />
            <Route
              path='agence-map'
              element={
                <ProtectedRoute allowedRoles={['admin', 'responsable_agence']} pageKey="agence-map" bypassIfAgence="Maison mère">
                  <AgenceObjectifMap />
                </ProtectedRoute>
              }
            />
            <Route
              path='commission-dashboard'
              element={
                <ProtectedRoute pageKey="commission-dashboard" requirePartner="octopus">
                  <CommissionDashboard />
                </ProtectedRoute>
              }
            />
            <Route path='outils' element={<ProtectedRoute pageKey="outils"><Outils /></ProtectedRoute>} />
            <Route path='infos' element={<ProtectedRoute pageKey="infos"><Infos /></ProtectedRoute>} />
            <Route path='formation' element={<ProtectedRoute pageKey="formation"><Training /></ProtectedRoute>} />
            <Route path='formation/octopus-energy' element={<ProtectedRoute pageKey="formation"><OctopusPresentation /></ProtectedRoute>} />
            <Route path='formation/ilek' element={<ProtectedRoute pageKey="formation"><IlekPresentation /></ProtectedRoute>} />
            <Route path='formation/quiz-octopus-vente' element={<ProtectedRoute><QuizOctopusVente /></ProtectedRoute>} />
            <Route path='organigramme' element={<ProtectedRoute pageKey="organigramme" allowedRoles={['admin']}><OrganigrammePage /></ProtectedRoute>} />
            <Route path='organisation' element={<ProtectedRoute allowedRoles={['admin']}><OrganisationAdmin /></ProtectedRoute>} />
            <Route path='voitures' element={<ProtectedRoute pageKey="voitures"><Voitures /></ProtectedRoute>} />
            <Route
              path='carburant'
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <FuelDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path='db-admin'
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <DatabaseAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path='seller-space'
              element={
                <ProtectedRoute pageKey="seller-space">
                  <SellerSpace />
                </ProtectedRoute>
              }
            />
            <Route
              path='mes-documents'
              element={
                <ProtectedRoute pageKey="seller-space">
                  <MesDocuments />
                </ProtectedRoute>
              }
            />
            <Route
              path='suivi-cartons'
              element={
                <ProtectedRoute pageKey="suivi-cartons">
                  <SuiviCartons />
                </ProtectedRoute>
              }
            />
            <Route
              path='suivi-rh'
              element={
                <ProtectedRoute pageKey="suivi-cartons">
                  <SuiviCartons />
                </ProtectedRoute>
              }
            />
            <Route
              path='admin'
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path='chat'
              element={
                <ProtectedRoute pageKey="chat">
                  <Chat />
                </ProtectedRoute>
              }
            />
            <Route
              path='quality-overview'
              element={
                <ProtectedRoute pageKey="quality-overview" requirePartner="octopus">
                  <QualityOverview />
                </ProtectedRoute>
              }
            />
            <Route path='ilek/dashboard' element={<ProtectedRoute pageKey="ilek-dashboard" requirePartner="ilek"><IlekDashboard /></ProtectedRoute>} />
            <Route path='ilek/contracts' element={<ProtectedRoute pageKey="ilek-contracts" requirePartner="ilek"><IlekContracts /></ProtectedRoute>} />
            <Route path='ilek/qualite'   element={<ProtectedRoute pageKey="ilek-qualite"   requirePartner="ilek"><IlekQualite   /></ProtectedRoute>} />
            <Route path='bouygues/dashboard' element={<ProtectedRoute pageKey="bouygues-dashboard" requirePartner="bouygues"><BouyguesDashboard /></ProtectedRoute>} />
            <Route path='bouygues/contracts' element={<ProtectedRoute pageKey="bouygues-contracts" requirePartner="bouygues"><BouyguesContracts /></ProtectedRoute>} />
            <Route path='assistant-ia' element={<ProtectedRoute pageKey="assistant-ia" bypassIfEmail="bo.rtdistribution@gmail.com"><AssistantIA /></ProtectedRoute>} />
            </Route>
          </Route>
        </Routes>
        </PermissionsProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;